import { useCallback, useEffect, useState } from 'react'
import {
  ArrowDown,
  ArrowUp,
  Loader2,
  RefreshCw,
  UserPlus,
  Users,
} from 'lucide-react'

import { PosCustomerCard, PosCustomerSearch } from '@/components/pos/PosCustomerSearch'
import { Button } from '@/components/ui/button'
import {
  addWalkinQueueEntry,
  assignWalkinQueueEntry,
  fetchWalkinQueue,
  reorderWalkinQueue,
} from '@/lib/api/walkinQueue'
import { fetchServices } from '@/lib/api/services'
import {
  buildAddQueuePayload,
  formatQueueEta,
  queueGuestLabel,
  reorderAfterMove,
  validateAddQueueForm,
} from '@/lib/calendar/walkinQueue'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { cn } from '@/lib/utils'

/**
 * @param {{
 *   stylists: import('@/types/api').Stylist[]
 *   entries?: import('@/types/api').WalkinQueueEntry[]
 *   onEntriesChange?: (entries: import('@/types/api').WalkinQueueEntry[]) => void
 *   services?: import('@/types/api').Service[]
 *   queueLoading?: boolean
 *   onRefreshQueue?: () => Promise<void>
 *   embedded?: boolean
 * }} props
 */
export function WalkinQueuePanel({
  stylists,
  entries: controlledEntries,
  onEntriesChange,
  services: controlledServices,
  queueLoading = false,
  onRefreshQueue,
  embedded = false,
}) {
  const isControlled = controlledEntries !== undefined
  const [internalEntries, setInternalEntries] = useState([])
  const [internalServices, setInternalServices] = useState([])
  const [loading, setLoading] = useState(!isControlled)
  const [submitting, setSubmitting] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const [error, setError] = useState('')
  const [addForm, setAddForm] = useState({
    customer: null,
    guestName: '',
    guestPhone: '',
    serviceId: '',
    stylistId: '',
    notes: '',
  })

  const entries = isControlled ? controlledEntries : internalEntries
  const services = controlledServices ?? internalServices

  const setEntries = useCallback(
    /** @param {import('@/types/api').WalkinQueueEntry[] | ((current: import('@/types/api').WalkinQueueEntry[]) => import('@/types/api').WalkinQueueEntry[])} next */
    (next) => {
      if (isControlled && onEntriesChange) {
        onEntriesChange(typeof next === 'function' ? next(controlledEntries ?? []) : next)
        return
      }

      setInternalEntries(next)
    },
    [controlledEntries, isControlled, onEntriesChange],
  )

  const loadQueue = useCallback(async () => {
    if (isControlled && onRefreshQueue) {
      await onRefreshQueue()
      return
    }

    setLoading(true)
    setError('')

    try {
      const [queueEntries, serviceItems] = await Promise.all([
        fetchWalkinQueue(),
        controlledServices ? Promise.resolve([]) : fetchServices(),
      ])
      setInternalEntries(queueEntries)
      if (!controlledServices) {
        setInternalServices(serviceItems.filter((service) => service.is_active))
      }
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to load walk-in queue.'))
    } finally {
      setLoading(false)
    }
  }, [controlledServices, isControlled, onRefreshQueue])

  useEffect(() => {
    if (!isControlled) {
      loadQueue()
    }
  }, [isControlled, loadQueue])

  /**
   * @param {number[]} orderedIds
   */
  async function handleReorder(orderedIds) {
    setSubmitting(true)
    setError('')

    try {
      const updated = await reorderWalkinQueue(orderedIds)
      setEntries(updated)
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to reorder queue.')
      await loadQueue()
    } finally {
      setSubmitting(false)
    }
  }

  /**
   * @param {number} entryId
   * @param {'up' | 'down'} direction
   */
  async function moveEntry(entryId, direction) {
    const orderedIds = reorderAfterMove(entries, entryId, direction)
    if (orderedIds.join(',') === entries.map((entry) => entry.id).join(',')) {
      return
    }

    await handleReorder(orderedIds)
  }

  /**
   * @param {number} entryId
   * @param {string} stylistId
   */
  async function handleAssign(entryId, stylistId) {
    setSubmitting(true)
    setError('')

    try {
      const updated = await assignWalkinQueueEntry(entryId, {
        stylist_id: stylistId ? Number(stylistId) : null,
      })
      setEntries((current) =>
        current.map((entry) => (entry.id === updated.id ? updated : entry)),
      )
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to assign stylist.')
      await loadQueue()
    } finally {
      setSubmitting(false)
    }
  }

  async function handleAddGuest(event) {
    event.preventDefault()

    const validationErrors = validateAddQueueForm(addForm)
    if (validationErrors.length > 0) {
      setError(validationErrors[0])
      return
    }

    setSubmitting(true)
    setError('')

    try {
      await addWalkinQueueEntry(buildAddQueuePayload(addForm))
      setAddForm({
        customer: null,
        guestName: '',
        guestPhone: '',
        serviceId: '',
        stylistId: '',
        notes: '',
      })
      setShowAddForm(false)
      if (isControlled && onRefreshQueue) {
        await onRefreshQueue()
      } else {
        await loadQueue()
      }
    } catch (err) {
      const fieldErrors = err?.response?.data?.errors
      if (fieldErrors) {
        setError(Object.values(fieldErrors).flat()[0] ?? 'Unable to add guest.')
      } else {
        setError(err?.response?.data?.message ?? 'Unable to add guest.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const activeStylists = stylists.filter((stylist) => stylist.is_active)

  return (
    <aside
      className={cn(
        'flex flex-col overflow-hidden',
        embedded
          ? 'max-h-none'
          : 'card-surface max-h-[min(720px,calc(100vh-14rem))]',
      )}
    >
      <div
        className={cn(
          'flex items-center justify-between border-b border-border/60 px-4 py-3',
          embedded && 'border-border/40 px-5',
        )}
      >
        <div>
          {embedded ? (
            <p className="text-[11px] text-muted-foreground">
              {entries.length} in queue · live wait times
            </p>
          ) : (
            <>
              <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Users className="h-4 w-4 text-primary" />
                Walk-in queue
              </p>
              <p className="text-[11px] text-muted-foreground">
                {entries.length} waiting · live ETA
              </p>
            </>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={loading || queueLoading || submitting}
            onClick={() => loadQueue()}
            aria-label="Refresh queue"
          >
            <RefreshCw className={cn('h-4 w-4', (loading || queueLoading) && 'animate-spin')} />
          </Button>
          <Button
            type="button"
            size="sm"
            variant={showAddForm ? 'secondary' : 'default'}
            disabled={submitting}
            onClick={() => setShowAddForm((current) => !current)}
          >
            <UserPlus className="mr-1 h-3.5 w-3.5" />
            {embedded ? 'Add walk-in' : 'Add'}
          </Button>
        </div>
      </div>

      {showAddForm ? (
        <form onSubmit={handleAddGuest} className="space-y-3 border-b border-border/60 p-4">
          {addForm.customer ? (
            <PosCustomerCard
              customer={addForm.customer}
              disabled={submitting}
              onClear={() => setAddForm((current) => ({ ...current, customer: null }))}
            />
          ) : (
            <>
              <PosCustomerSearch
                disabled={submitting}
                onSelectCustomer={(customer) =>
                  setAddForm((current) => ({
                    ...current,
                    customer,
                    stylistId: customer.preferred_stylist_id
                      ? String(customer.preferred_stylist_id)
                      : current.stylistId,
                  }))
                }
              />
              <label className="block">
                <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  Or walk-in name
                </span>
                <input
                  type="text"
                  disabled={submitting}
                  value={addForm.guestName}
                  onChange={(event) =>
                    setAddForm((current) => ({ ...current, guestName: event.target.value }))
                  }
                  placeholder="Guest without profile"
                  className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
                />
              </label>
            </>
          )}

          <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Service
            </span>
            <select
              disabled={submitting}
              value={addForm.serviceId}
              onChange={(event) =>
                setAddForm((current) => ({ ...current, serviceId: event.target.value }))
              }
              className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">Optional</option>
              {services.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.name}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Preferred stylist
            </span>
            <select
              disabled={submitting}
              value={addForm.stylistId}
              onChange={(event) =>
                setAddForm((current) => ({ ...current, stylistId: event.target.value }))
              }
              className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">Any</option>
              {activeStylists.map((stylist) => (
                <option key={stylist.id} value={stylist.id}>
                  {stylist.name}
                </option>
              ))}
            </select>
          </label>

          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? 'Adding…' : 'Add to queue'}
          </Button>
        </form>
      ) : null}

      {error ? (
        <div className="border-b border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700">
          {error}
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {(loading || queueLoading) && entries.length === 0 ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading queue…
          </div>
        ) : entries.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            No walk-ins waiting. Tap Add to queue a guest.
          </p>
        ) : (
          <ul className="space-y-2">
            {entries.map((entry, index) => (
              <li
                key={entry.id}
                className="rounded-xl border border-border/60 bg-card p-3 shadow-sm"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-foreground">
                      #{entry.position ?? index + 1} · {queueGuestLabel(entry)}
                    </p>
                    <p className="truncate text-[11px] text-muted-foreground">
                      {entry.service?.name ?? 'Service TBD'}
                    </p>
                    <p className="mt-1 text-xs font-semibold text-primary">
                      {formatQueueEta(entry.estimated_wait_minutes)}
                    </p>
                  </div>

                  <div className="flex shrink-0 flex-col gap-1">
                    <button
                      type="button"
                      disabled={submitting || index === 0}
                      onClick={() => moveEntry(entry.id, 'up')}
                      className="flex h-7 w-7 items-center justify-center rounded-lg border border-border/60 text-muted-foreground hover:bg-muted/50 disabled:opacity-40"
                      aria-label="Move up"
                    >
                      <ArrowUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      disabled={submitting || index === entries.length - 1}
                      onClick={() => moveEntry(entry.id, 'down')}
                      className="flex h-7 w-7 items-center justify-center rounded-lg border border-border/60 text-muted-foreground hover:bg-muted/50 disabled:opacity-40"
                      aria-label="Move down"
                    >
                      <ArrowDown className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                <label className="mt-3 block">
                  <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Stylist
                  </span>
                  <select
                    disabled={submitting}
                    value={entry.stylist_id ? String(entry.stylist_id) : ''}
                    onChange={(event) => handleAssign(entry.id, event.target.value)}
                    className="w-full rounded-lg border border-input bg-background px-2 py-1.5 text-xs"
                  >
                    <option value="">Unassigned</option>
                    {activeStylists.map((stylist) => (
                      <option key={stylist.id} value={stylist.id}>
                        {stylist.name}
                      </option>
                    ))}
                  </select>
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  )
}
